import { useMemo } from "react";
import {
  Box,
  ColumnLayout,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import { CanonicalBusinessEvent } from "../../types/cbes";
import {
  calculateBundleRevenueSummary,
  generateBundleRevenueTimeline,
  hasPrepaidEvents,
  hasBundleOrder,
  BundleRevenueTimelineEntry,
} from "../../helpers/bundleRevenueUtils";
import { formatCurrency, formatDateTime } from "../../helpers/dbdUtils";

interface BundleRevenueTimelineProps {
  cbes: CanonicalBusinessEvent[];
  productCount?: number;
  dbdProducts?: { [key: string]: any };
}

const EVENT_BADGE: Record<string, { type: "info" | "success" | "error" | "pending"; label: string }> = {
  prepaid: { type: "info", label: "Prepaid" },
  fulfilled: { type: "success", label: "Fulfilled" },
  refunded: { type: "error", label: "Refunded" },
  pending: { type: "pending", label: "Pending" },
};

const formatAmount = (item: BundleRevenueTimelineEntry): string => {
  if (item.eventType === "pending") return "-";
  const prefix = item.amount >= 0 ? "+" : "";
  return `${prefix}${formatCurrency(Math.abs(item.amount), item.currencyCode)}`;
};

const COLUMN_DEFINITIONS: TableProps<BundleRevenueTimelineEntry>["columnDefinitions"] = [
  {
    id: "date",
    header: "Date",
    cell: (item) => (item.date ? formatDateTime(item.date) : "-"),
    sortingField: "date",
  },
  {
    id: "product",
    header: "Product",
    cell: (item) => (
      <Box variant={item.eventType === "prepaid" ? "strong" : "span"}>{item.productId}</Box>
    ),
  },
  {
    id: "amount",
    header: "Amount",
    cell: (item) => {
      const color =
        item.eventType === "prepaid"
          ? undefined
          : item.eventType === "refunded"
            ? "text-status-error"
            : undefined;
      return (
        <Box variant={item.eventType === "prepaid" ? "strong" : "span"} color={color}>
          {formatAmount(item)}
        </Box>
      );
    },
  },
  {
    id: "deferred",
    header: "Deferred Balance",
    cell: (item) => (
      <Box color={item.deferredBalance > 0 ? "text-status-warning" : "text-status-success"}>
        {formatCurrency(item.deferredBalance, item.currencyCode)}
      </Box>
    ),
  },
  {
    id: "event",
    header: "Event",
    cell: (item) => {
      const badge = EVENT_BADGE[item.eventType] || EVENT_BADGE.pending;
      return <StatusIndicator type={badge.type}>{badge.label}</StatusIndicator>;
    },
  },
];

export function BundleRevenueTimeline({ cbes, productCount, dbdProducts }: BundleRevenueTimelineProps) {
  const summary = useMemo(() => {
    if (!cbes?.length || !hasBundleOrder(cbes) || !hasPrepaidEvents(cbes)) return null;
    return calculateBundleRevenueSummary(cbes, productCount);
  }, [cbes, productCount]);

  const timeline = useMemo(() => {
    if (!cbes?.length || !hasBundleOrder(cbes) || !hasPrepaidEvents(cbes)) return [];
    return generateBundleRevenueTimeline(cbes, dbdProducts);
  }, [cbes, dbdProducts]);

  if (!summary || !timeline.length) return null;

  return (
    <SpaceBetween size="l">
      <ColumnLayout columns={3} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">Initial Deferred Revenue</Box>
          <Box variant="h3">{formatCurrency(summary.initialDeferredRevenue, summary.currencyCode)}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Current Deferred Balance</Box>
          <Box variant="h3" color={summary.currentDeferredBalance > 0 ? "text-status-warning" : "text-status-success"}>
            {formatCurrency(summary.currentDeferredBalance, summary.currencyCode)}
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Fulfilment</Box>
          <SpaceBetween size="xs">
            <ProgressBar
              value={summary.completionPercentage}
              status={summary.completionPercentage === 100 ? "success" : "in-progress"}
            />
            <Box variant="small">
              {summary.fulfilledCount} of {summary.totalCount} products ({summary.completionPercentage}%)
            </Box>
          </SpaceBetween>
        </div>
      </ColumnLayout>

      <Table
        columnDefinitions={COLUMN_DEFINITIONS}
        items={timeline}
        variant="embedded"
        empty={
          <Box textAlign="center" color="inherit">
            <b>No timeline data</b>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
